import { TranslatableLabelVO } from './../common/translation/translatable-label.vo';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { BusinessUnit, Country, Language, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { CountryVO } from '../locale/country.vo';
import { LanguageVO } from '../locale/language.vo';
import { Model } from '../../model/model';
import { AttachmentService } from '../../command/core/attachment/attachment.service';
import { Observable } from 'rxjs';
import { ViewData } from '../../model/view.data';
import { IVo, entity, field } from '@wizeoo/akm-compute-lib';

/**
 * Une unité fonctionnelle
 */
@entity('wizeoo.allkey.core.message.BusinessUnit', 'BU')
export class BusinessUnitVO implements IVo {
  
  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }
  
  /** L'objet source provenant du back */
  private _source: BusinessUnit;
  public get source(): BusinessUnit {
    return this._source;
  }
  public set source(value: BusinessUnit) {
    this._source = value;
  }

  constructor(model: Model, pSource?: BusinessUnit) {
    this._model = model;
    this._source = null;
    if (pSource) {
      this._source = pSource;

    } else {
      this._source = new BusinessUnit();
      this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
    }
  }

  /* ################################################
              Accesseurs primitifs
     ################################################ */

  @field('string')
  public get parentCode(): string {
    // BU de rattachement
    return this._source.parentCode;
  }
  public set parentCode(value: string) {
    this._source.parentCode = value;
  }

  @field('string')
  public get businessUnitCode(): string {
    // code de la BU
    return this._source.businessUnitCode;
  }
  public set businessUnitCode(value: string) {
    this._source.businessUnitCode = value;
  }

  @field('string')
  public get name(): string {
    return this._source.name;
  }
  public set name(value: string) {
    this._source.name = value;
  }

  @field('string')
  public get supportMail(): string {
    return this._source.supportMail;
  }
  public set supportMail(value: string) {
    this._source.supportMail = value;
  }

  @field('string')
  public get helpURL(): string {
    return this._source.helpURL;
  }
  public set helpURL(value: string) {
    this._source.helpURL = value;
  }

  @field('TranslatedEmb')
  public set description(value: TranslatedEmb) {
    this._source.description = value;
  }
  public get description(): TranslatedEmb {
    return this._source.description;
  }

  // Id technique
  public get id(): number {
    return this._source.id;
  }
  public set id(value: number) {
    this._source.id = value;
  }

  // UID technique
  public get uid(): string {
    return this._source.uid;
  }
  public set uid(value: string) {
    this._source.uid = value;
  }

  // code domaine (root), technique
  public get domain(): string {
    return this._source.domain;
  }
  public set domain(value: string) {
    this._source.domain = value;
  }

  @field('boolean')
  public get deprecated(): boolean {
    return this._source.deprecated;
  }
  public set deprecated(value: boolean) {
    this._source.deprecated = value;
  }

  @field('boolean')
  public get deleted(): boolean {
    return this._source.deleted;
  }
  public set deleted(value: boolean) {
    this._source.deleted = value;
  }

  @field('boolean')
  public get deactivated(): boolean {
    // flag de désactivation
    return this._source.deactivated;
  }
  public set deactivated(value: boolean) {
    this._source.deactivated = value;
  }

  @field('string')
  public get defaultLanguageCode(): string {
    // langue par défaut
    return this._source.defaultLanguageCode;
  }
  public set defaultLanguageCode(value: string) {
    this._source.defaultLanguageCode = value;
  }

  public get defaultCountryCode(): string {
    // Pays par défaut
    return this._source.defaultCountryCode;
  }
  public set defaultCountryCode(value: string) {
    this._source.defaultCountryCode = value;
  }

  public get version(): number {
    // technique
    return this._source.version;
  }
  public set version(value: number) {
    this._source.version = value;
  }

  public get minRevNumber(): number {
    // technique
    return this._source.minRevNumber;
  }
  public set minRevNumber(value: number) {
    this._source.minRevNumber = value;
  }


  // collection de langues (pseudomap)
  @field('Language', true)
  public get languages(): Language[] {
    return this._source.languages ? Object.values(this._source.languages) : [];
  }
  public set languages(languages: Language[]) {
    this._source.languages = {};
    // convertion de Language[] en {[key: string]: Language}
    languages.map((language) => {
      this._source.languages[language.code] = language;
    });
  }

  // collection de pays (pseudomap)
  @field('Country', true)
  public get countries(): Country[] {
    return this._source.countries ? Object.values(this._source.countries) : [];
  }
  public set countries(countries: Country[]) {
    this._source.countries = {};
    // convertion de Country[] en {[key: string]: Country}
    countries.map((country) => {
      this._source.countries[country.code] = country;
    });
  }

  @field('string')
  public get icon(): string {
    return this._source.icon;
  }

  public set icon(pValue: string) {
    this._source.icon = pValue;
  }

  /* ################################################
                    Accesseurs composites
  ################################################ */

  public get uniqueKey(): string {
    return [this.domain, this.businessUnitCode].join(ViewData.KEY_SEPARATOR);
  }


  public get exist(): boolean {
    // l'objet existe en BDD (id)
    return this.id > 0;
  }

  public get fullLabel(): string {
    // code + label pour l'affichage
    return this.businessUnitCode + '-' + this.name;
  }

  public get isRoot(): boolean {
    // true pour la racine
    return this.businessUnitCode === this.domain && this.exist;
  }

  /** retourne l'icone si existe, sinon le média par défaut */
  public get iconOrDie(): Observable<string> {
    // console.log('BuIcon', this.icon, this.domain);
    return AttachmentService.getInstance().iconOrDie(this.icon, this.domain, this.businessUnitCode, ViewData.wildcardMainVision);
  }

  /** accesseur au pays par défaut en mode VO */
  public get defaultCountryVO(): CountryVO {
    let result: CountryVO = null;
    if (this.defaultCountryCode && this.model.countryMap) {
      result = this.model.countryMap.get(this.defaultCountryCode);
    }
    return result;
  }

  /** accesseur sur la liste des pays en mode VO */
  public get countriesVO(): CountryVO[] {
    let result: CountryVO[] = null;
    if (this.countries && this.model.countryMap) {
      result = this.countries.map((cn) => this.model.countryMap.get(cn.code));
    }
    return result;
  }
  public set countriesVO(value: CountryVO[]) {
    let result: Country[] = null;
    if (value) {
      result = value.map((cn) => cn.source);
    }
    this.countries = result;
  }

  /** accesseur sur la langue par défaut en mode VO */
  public get defaultLanguagesVO(): LanguageVO {
    let result: LanguageVO = null;
    if (this.defaultLanguageCode && this.model.languageMap) {
      result = this.model.languageMap.get(this.defaultLanguageCode);
    }
    return result;
  }

  /** accesseur sur la liste des langues en mode VO */
  public get languagesVO(): LanguageVO[] {
    let result: LanguageVO[] = null;
    if (this.languages && this.model.languageMap) {
      result = this.languages.map((lg) => this.model.languageMap.get(lg.code));
    }
    return result;
  }
  /** accesseur sur la liste des pays en mode VO */
  public set languagesVO(value: LanguageVO[]) {
    let result: Language[] = null;
    if (value) {
      // console.log('setlan', value);
      result = value.map((cn) => cn.source);
    }
    this.languages = result;
  }
  private _firstDayOfWeek: number = 1;
  public set firstDayOfWeek(val: number) {
    this._firstDayOfWeek = val;
  }
  /** Premier jour de la semaine */
  public get firstDayOfWeek(): number {
    return this._firstDayOfWeek;
  }

  /** description en mode VO */
  private _descriptionVO: TranslatableLabelVO;
  public get descriptionVO(): TranslatableLabelVO {
    if (!this._descriptionVO) {
      this._descriptionVO = this.model.localeEngine.newTranslatableVO(this, 'description');
    }
    return this._descriptionVO;
  }

  /**
   * Accès au libellé traduit
   */
  public get trsDescription(): string {
    return this.model.localeEngine.translate(this.description);
  }

  /** clé de filtre unifiée domaine / Bu */
  public get filterKey(): string {
    return this.name + '|' + this.businessUnitCode + '|' + this.trsDescription;
  }

  /** retrouve la BU "domain" */
  public get domainVO(): BusinessUnitVO {
    let result: BusinessUnitVO = this.model.businessUnitEngine.getByBusinessUnitCode(this.domain, this.domain);
    return result;
  }

  @field('string')
  public get mail(): string {
    return this._source.mail;
  }
  public set mail(value: string) {
    this._source.mail = value;
  }

}
